import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Stack,
    CardHeader
} from '@mui/material';
import {
    LocalShipping,
    CheckCircle,
    InfoOutlined,
    Timeline,
    DateRange
} from '@mui/icons-material';
import { useForm } from "react-hook-form";
import { RHFTextField, FormProvider } from "../common/hook-form";
import { apiConstants, getGeneric } from '../common/constants';
import { useSnackbar } from 'notistack';
import PageTitle from '../components/PageTitle';
import { LoadingButton } from '@mui/lab';
import CopyIcon from '../common/CopyIcon';
import { Composition, ContributionLegend, HeadExplain } from '../pages/AllocationConfig/PrismContributions';

const AwbEddDetails = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [prism, setPrism] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    const onSubmit = async (formData) => {
        if (!formData.awbNumber) {
            setData(null);
            enqueueSnackbar('Please enter a valid AWB number', { variant: 'error' });
            return;
        }
        setLoading(true);
        setPrism(null);
        try {
            const response = await getGeneric(`${apiConstants.GET_EDD_REMARKS}?awbNumber=${formData.awbNumber}`);
            setData(response?.data);
            enqueueSnackbar('AWB details fetched successfully', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || 'An error occurred while fetching data', { variant: 'error' });
            console.log(err, 'err');
            setData(null);
        } finally {
            setLoading(false);
        }
        // PRISM iEDD (shadow) — best-effort; no record / PRISM down = section hidden
        try {
            const p = await getGeneric(`prism/iedd/${encodeURIComponent(formData.awbNumber)}`);
            setPrism(p?.data);
        } catch (e) {
            setPrism(null);
        }
    };

    const tierColor = (t) => (t === 'HIGH' ? 'success' : t === 'MEDIUM' ? 'warning' : 'default');

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'DELIVERED':
                return 'success';
            case 'IN_TRANSIT':
                return 'primary';
            case 'PENDING':
                return 'warning';
            case 'CANCELLED':
                return 'error';
            default:
                return 'default';
        }
    };

    const methods = useForm({
        defaultValues: {
            awbNumber: ''
        }
    });
    const { handleSubmit } = methods;
    console.log(data, 'data');

    return (
        <>
            <PageTitle>AWB EDD Details Tracker</PageTitle>
            <Card elevation={2}>
                <CardHeader
                    title="View EDD Details using AWB Number"
                />
                <CardContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6}>
                                <RHFTextField
                                    name="awbNumber"
                                    label="AWB Number"
                                    placeholder="Enter AWB number"
                                    fullWidth
                                    required
                                    size="medium"
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    color="warning"
                                    size="medium"
                                    loading={loading}
                                    sx={{ borderRadius: 0.5 }}
                                    startIcon={<LocalShipping />}
                                >
                                    Track AWB
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>
            <Card elevation={2} sx={{ mt: 2 }}>
                <CardContent>
                    {data ? (
                        <Grid container spacing={2}>
                            {/* Summary Info */}
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={3}>
                                                <Typography variant="body2" color="text.secondary">AWB Number</Typography>
                                                <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                                                    {data.awb}<CopyIcon text={data.awb} />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <Typography variant="body2" color="text.secondary">Status</Typography>
                                                <Chip
                                                    label={data.orderStatus}
                                                    color={getStatusColor(data.orderStatus)}
                                                    size="small"
                                                    sx={{ mt: 0.5, fontWeight: 'bold' }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3.5}>
                                                <Typography variant="body2" color="text.secondary">Expected Pickup</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                                                    {data.epd}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={3.5}>
                                                <Typography variant="body2" color="text.secondary">Expected Delivery</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                    {data.edd}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* PRISM iEDD (shadow) — side-by-side with the current EDD above */}
                            {prism?.iedd_date && (
                                <Grid item xs={12}>
                                    <Card elevation={2}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Typography variant="h6" gutterBottom color="primary"
                                                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <DateRange />
                                                PRISM iEDD (shadow — not shown to buyers)
                                            </Typography>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={3}>
                                                    <Typography variant="body2" color="text.secondary">iEDD</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                        {prism.iedd_date}
                                                        {prism.iedd_range_end ? ` – ${prism.iedd_range_end}` : ''}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <Typography variant="body2" color="text.secondary">Confidence</Typography>
                                                    <Chip size="small" label={prism.confidence_tier || '—'}
                                                        color={tierColor(prism.confidence_tier)} sx={{ mt: 0.5 }} />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <Typography variant="body2" color="text.secondary">Attribution</Typography>
                                                    <Typography variant="body2">{prism.attribution_level || '—'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography variant="body2" color="text.secondary">Shown EDD / Delivered</Typography>
                                                    <Typography variant="body2">
                                                        {prism.shown_edd || '—'} / {prism.delivered_at ? String(prism.delivered_at).slice(0, 10) : '—'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            {/* How the date was composed, and what moved the model terms.
                                                Same components the PRISM admin panel uses, so one AWB reads
                                                identically on both surfaces. */}
                                            <Box sx={{ mt: 1.5 }}>
                                                <Composition c={prism.components} />
                                                {prism.explain && (
                                                    <>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            What moved the estimate
                                                        </Typography>
                                                        <HeadExplain title="Transit" ex={prism.explain.transit} dense />
                                                        <HeadExplain title="Warehouse" ex={prism.explain.wh} dense />
                                                        <HeadExplain title="NDR re-attempt" ex={prism.explain.mobility} dense />
                                                        <ContributionLegend />
                                                    </>
                                                )}
                                            </Box>
                                            {(prism.history || []).length > 0 && (
                                                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                                                    <Typography variant="body2" color="text.secondary">Promise revisions</Typography>
                                                    {prism.history.map((h, i) => (
                                                        <Typography key={i} variant="caption">
                                                            {i + 1}. [{h.update_reason}] {h.iedd_date}
                                                            {h.iedd_range_end ? ` – ${h.iedd_range_end}` : ''} ({h.confidence_tier}) at {h.updated_at}
                                                        </Typography>
                                                    ))}
                                                    <Typography variant="caption">
                                                        {(prism.history || []).length + 1}. [{prism.update_reason} — current] {prism.iedd_date}
                                                        {prism.iedd_range_end ? ` – ${prism.iedd_range_end}` : ''} ({prism.confidence_tier})
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Remarks Section */}
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            color="primary"
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
                                        >
                                            <Timeline />
                                            Processing Remarks
                                        </Typography>
                                        <Stack spacing={1}>
                                            {data?.remarks ? data?.remarks.map((remark, index) => (
                                                <Paper
                                                    key={index}
                                                    elevation={0}
                                                    sx={{
                                                        p: 1.5,
                                                        backgroundColor: index % 2 === 0 ? 'grey.50' : 'background.paper',
                                                        border: '1px solid',
                                                        borderColor: 'grey.200',
                                                        borderRadius: 1
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Chip
                                                            label={index + 1}
                                                            size="small"
                                                            color="primary"
                                                            sx={{ minWidth: 30, height: 24 }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                lineHeight: 1.4,
                                                                flex: 1
                                                            }}
                                                        >
                                                            {remark}
                                                        </Typography>
                                                    </Box>
                                                </Paper>
                                            )) : <Typography variant="body2" sx={{ fontStyle: 'italic' }}>No remarks available</Typography>}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2">No information available</Typography>
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>
        </>
    );
};

export default AwbEddDetails;
